import type { ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, FormField, Input, Toggle } from '@playerbase/ui';
import type { RatingDisplay } from '@playerbase/types';

import {
  type BuilderField,
  type BuilderFieldKind,
  type UseFormBuilderState,
} from '../../../games/use-form-builder-state.js';
import { ShortTextConfig } from './field-config/short-text.js';
import { LongTextConfig } from './field-config/long-text.js';
import { RatingConfig } from './field-config/rating.js';
import { SelectConfig } from './field-config/select.js';
import { SeverityConfig } from './field-config/severity.js';
import { BinaryConfig } from './field-config/binary.js';
import { NpsConfig } from './field-config/nps.js';

const KIND_LABEL: Record<BuilderFieldKind, string> = {
  short_text: 'Short text',
  long_text: 'Long text',
  rating: 'Rating',
  select: 'Select',
  severity: 'Severity',
  binary: 'Binary',
  nps: 'NPS',
};

const FIELD_LABEL_CLASS =
  'font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted';

interface FieldRowProps {
  field: BuilderField;
  builder: UseFormBuilderState;
  // True for ~1.2s after the field is added; drives the one-shot accent
  // halo so the eye finds the freshly-scrolled-to row.
  flashing?: boolean;
}

// Sortable row inside the SortableContext. Tracks the dnd-kit transform +
// transition; ghosts itself out while it's the active drag source so
// the DragOverlay copy is the only visual the user follows.
export function FieldRow({ field, builder, flashing = false }: FieldRowProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: field.clientId });

  // Halo fades out in ~1s once `flashing` flips back to false. The base
  // box-shadow has a 1s ease-out transition so the only animated state
  // is the disappear.
  const flashShadow = flashing
    ? '0 0 0 4px rgba(168, 224, 99, 0.5)'
    : '0 0 0 0 rgba(168, 224, 99, 0)';
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition:
      transition !== undefined && transition !== null
        ? `${transition}, box-shadow 1s ease-out`
        : 'box-shadow 1s ease-out',
    boxShadow: flashShadow,
    // The DragOverlay renders a vivid copy at the cursor; the source
    // row stays in the list as a placeholder so layout shifts smoothly,
    // but we fade it out so users only follow the overlay.
    opacity: isDragging ? 0.35 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-field-id={field.clientId}
      className="rounded-md border border-border bg-surface-3"
    >
      <FieldRowBody
        field={field}
        builder={builder}
        dragHandle={
          <button
            type="button"
            aria-label="Drag to reorder"
            className="mt-1 cursor-grab text-text-muted hover:text-text active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            ⋮⋮
          </button>
        }
      />
    </li>
  );
}

// Floating copy used inside <DragOverlay>. Renders the same body but at
// its own natural size, with a stronger surface + shadow so it visually
// detaches from the list while being dragged. No useSortable here -
// the overlay is positioned by dnd-kit directly.
export function FieldRowOverlay({
  field,
  builder,
}: Omit<FieldRowProps, 'flashing'>): JSX.Element {
  return (
    <div className="rounded-md border border-border-strong bg-surface shadow-md">
      <FieldRowBody
        field={field}
        builder={builder}
        dragHandle={
          <span
            aria-hidden
            className="mt-1 cursor-grabbing text-text-muted"
          >
            ⋮⋮
          </span>
        }
      />
    </div>
  );
}

interface FieldRowBodyProps {
  field: BuilderField;
  builder: UseFormBuilderState;
  dragHandle: ReactNode;
}

// Inner content shared between the sortable row and the drag overlay.
// Holds the kind chip, inputs, per-kind config, and required toggle. The
// drag handle is supplied by the caller because the sortable wrapper
// needs to attach listeners while the overlay does not.
function FieldRowBody({ field, builder, dragHandle }: FieldRowBodyProps): JSX.Element {
  // NPS is fixed-shape: the question, helper text, and required flag are
  // seeded from NPS_FIELD_DEFAULTS and not editable. We render only the
  // kind chip, the explainer card, and Remove.
  const isFixedShape = field.kind === 'nps';

  return (
    <div className="flex items-start gap-3 p-4">
      {dragHandle}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <span className="rounded-sm border border-border bg-surface px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            {KIND_LABEL[field.kind]}
          </span>
          <Button
            variant="ghost"
            type="button"
            onClick={() => builder.removeField(field.clientId)}
            aria-label="Remove field"
          >
            Remove
          </Button>
        </div>

        {isFixedShape ? (
          <FixedShapeFieldSummary field={field} />
        ) : (
          <>
            <FormField>
              <FormField.Label className={FIELD_LABEL_CLASS} data-required>
                Question
              </FormField.Label>
              <FormField.Control>
                <Input
                  value={field.title}
                  onChange={(e) => builder.updateFieldTitle(field.clientId, e.target.value)}
                  maxLength={45}
                  placeholder="What did you think of the new boss fight?"
                />
              </FormField.Control>
              <FormField.HelpText>
                Up to 45 characters (Discord modal limit).
              </FormField.HelpText>
            </FormField>

            <FormField>
              <FormField.Label className={FIELD_LABEL_CLASS}>Helper text</FormField.Label>
              <FormField.Control>
                <Input
                  value={field.description ?? ''}
                  onChange={(e) =>
                    builder.updateFieldDescription(
                      field.clientId,
                      e.target.value === '' ? null : e.target.value,
                    )
                  }
                  maxLength={100}
                  placeholder="Optional context for the player"
                />
              </FormField.Control>
              <FormField.HelpText>
                Shown under the question in the Discord modal. Up to 100 characters.
              </FormField.HelpText>
            </FormField>
          </>
        )}

        <FieldKindConfig field={field} builder={builder} />

        {isFixedShape ? null : (
          <div className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2">
            <span className="text-sm text-text">Required</span>
            <Toggle
              checked={field.required}
              onCheckedChange={(checked) => builder.updateFieldRequired(field.clientId, checked)}
              aria-label={field.required ? 'Required' : 'Optional'}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Read-only summary used when the field's title / description / required
// flag are seeded from constants. Today this is just NPS; recruit will
// land here in story 10.
function FixedShapeFieldSummary({ field }: { field: BuilderField }): JSX.Element {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-surface px-3 py-2">
      <span className={FIELD_LABEL_CLASS}>Question</span>
      <span className="text-sm text-text">{field.title}</span>
      {field.description !== null && field.description.length > 0 ? (
        <>
          <span className={`mt-2 ${FIELD_LABEL_CLASS}`}>Helper text</span>
          <span className="text-xs text-text-muted">{field.description}</span>
        </>
      ) : null}
    </div>
  );
}

function FieldKindConfig({
  field,
  builder,
}: {
  field: BuilderField;
  builder: UseFormBuilderState;
}): JSX.Element | null {
  switch (field.kind) {
    case 'short_text':
      return <ShortTextConfig />;
    case 'long_text':
      return <LongTextConfig />;
    case 'severity':
      return <SeverityConfig />;
    case 'binary': {
      const config = field.config as { trueLabel?: string; falseLabel?: string };
      return (
        <BinaryConfig
          trueLabel={config.trueLabel}
          falseLabel={config.falseLabel}
          onChange={(which, value) =>
            builder.setBinaryLabel(field.clientId, which, value)
          }
        />
      );
    }
    case 'nps':
      return <NpsConfig />;
    case 'rating': {
      const config = field.config as { display?: RatingDisplay };
      return (
        <RatingConfig
          display={config.display}
          onChange={(display) => builder.setRatingDisplay(field.clientId, display)}
        />
      );
    }
    case 'select': {
      const config = field.config as { options: string[]; maxValues?: number };
      return (
        <SelectConfig
          options={config.options}
          maxValues={config.maxValues}
          onReplace={(index, value) =>
            builder.replaceSelectOption(field.clientId, index, value)
          }
          onAdd={() => builder.addSelectOption(field.clientId)}
          onRemove={(index) => builder.removeSelectOption(field.clientId, index)}
          onMaxValuesChange={(maxValues) =>
            builder.setSelectMaxValues(field.clientId, maxValues)
          }
        />
      );
    }
  }
}
